import { domainError, type DomainError } from '../shared/domain-error';
import type {
  FloorId,
  ProjectId,
  RoomId,
  RoomProgressId,
  ServiceId,
  UnitPositionId,
  UnitTypeId,
  UserId,
} from '../shared/ids';
import { err, ok, type Result } from '../shared/result';
import { TAKEOFF_ERROR, TAKEOFF_ERROR_MESSAGE } from './errors';

/**
 * O avanço físico por cômodo (Fase 34, etapa 1 · D4, D8, D9, D13 · RN-441 a RN-445).
 *
 * O cômodo do cronograma é o cômodo da tipologia numa posição de um pavimento: pavimento ×
 * posição × cômodo (RN-441) — "o banheiro social do apto 04 do 12º". Ele não é gravado à parte:
 * é lido do desenho, e o que se grava é o **percentual** dele num serviço.
 *
 * - **A trilha** (RN-443): cada apontamento grava o dia, o percentual e quem informou, e vale o
 *   último. O percentual pode descer (retrabalho, correção), e a trilha diz quando.
 * - **Só avanço físico** (D8): o percentual não vira executado do dia; a medição continua à
 *   parte, dizendo quanto a equipe produziu. O cômodo diz onde a obra está.
 * - **Não se aplica** (RN-444): vale para o cômodo da tipologia num serviço — em todos os aptos
 *   dela —, e tira o cômodo da conta.
 * - **O avanço do item** (RN-445): com algum cômodo informado, é a média pesada pela quantidade
 *   (`room-quantity.ts`), e vence a medição.
 */

// ── O apontamento ───────────────────────────────────────────────────────────────

export interface RoomProgress {
  readonly id: RoomProgressId;
  readonly projectId: ProjectId;
  /** O pavimento — o andar de verdade, e não o desenho que ele usa. */
  readonly floorId: FloorId;
  /** A posição do desenho no pavimento: o apto, ou a área comum (RN-441, D12). */
  readonly positionId: UnitPositionId;
  /** O cômodo da tipologia da posição. */
  readonly roomId: RoomId;
  readonly serviceId: ServiceId;
  /** AAAA-MM-DD — o dia do apontamento. */
  readonly date: string;
  /** 0 a 100, com duas casas. */
  readonly percent: number;
  /** Quem informou. Ausente só no que vier de fora da tela. */
  readonly recordedByUserId?: UserId;
  readonly createdAt: string;
}

/** O cômodo da tipologia marcado "não se aplica" num serviço (RN-444). */
export interface RoomNotApplicable {
  readonly projectId: ProjectId;
  readonly unitTypeId: UnitTypeId;
  readonly roomId: RoomId;
  readonly serviceId: ServiceId;
  readonly markedByUserId?: UserId;
  readonly createdAt: string;
}

/** `posição|cômodo` — o cômodo do apto dentro de um pavimento e serviço já escolhidos. */
export function floorRoomKey(positionId: string, roomId: string): string {
  return `${positionId}|${roomId}`;
}

/**
 * O que vale de cada cômodo (RN-443): o último apontamento — o do dia mais recente e, no mesmo
 * dia, o gravado por último. Com `onDate`, o que valia naquele dia: os apontamentos de depois
 * ficam de fora, e a tela de um dia passado mostra o que se sabia então.
 *
 * Recebe os apontamentos de **um** pavimento × serviço; a chave é `floorRoomKey`.
 */
export function latestRoomProgress(
  records: readonly RoomProgress[],
  onDate?: string,
): ReadonlyMap<string, RoomProgress> {
  const ultimo = new Map<string, RoomProgress>();
  for (const record of records) {
    if (onDate !== undefined && record.date > onDate) continue;
    const chave = floorRoomKey(record.positionId, record.roomId);
    const atual = ultimo.get(chave);
    if (atual === undefined || depois(record, atual)) ultimo.set(chave, record);
  }
  return ultimo;
}

function depois(a: RoomProgress, b: RoomProgress): boolean {
  if (a.date !== b.date) return a.date > b.date;
  if (a.createdAt !== b.createdAt) return a.createdAt > b.createdAt;
  return a.id > b.id;
}

/**
 * O percentual que a tela mandou, conferido: de 0 a 100, com duas casas. A casa a mais é
 * arredondada, e não recusada — 33,333% é um terço, e ninguém o digita por engano.
 */
export function checkRoomPercent(percent: number): Result<number, DomainError> {
  if (!Number.isFinite(percent) || percent < 0 || percent > 100) {
    return err(
      domainError(
        TAKEOFF_ERROR.INVALID_ROOM_PERCENT,
        TAKEOFF_ERROR_MESSAGE[TAKEOFF_ERROR.INVALID_ROOM_PERCENT] ?? '',
        { percent },
      ),
    );
  }
  return ok(Math.round(percent * 100) / 100);
}

// ── O avanço do item (RN-445) ───────────────────────────────────────────────────

/** Um cômodo na conta do item: o peso dele e o percentual que vale, se foi informado. */
export interface WeighedRoomProgress {
  readonly weight: number;
  readonly percent?: number;
}

/**
 * O avanço do item pelos cômodos (RN-445): a média dos percentuais pesada pela quantidade de
 * cada cômodo. Recebe só os que contam — o "não se aplica" sai antes (RN-444).
 *
 * `undefined` quando nenhum cômodo foi informado: aí vale o de hoje, pela medição ou o digitado.
 * Com algum informado, o que ainda não foi conta 0%. Nunca passa de 100.
 */
export function progressFromRooms(rooms: readonly WeighedRoomProgress[]): number | undefined {
  if (!rooms.some((room) => room.percent !== undefined)) return undefined;
  const pesoTotal = rooms.reduce((soma, room) => soma + room.weight, 0);
  if (pesoTotal <= 0) return undefined;
  const pesado = rooms.reduce((soma, room) => soma + room.weight * (room.percent ?? 0), 0);
  return Math.min(100, Math.round((pesado / pesoTotal) * 100) / 100);
}

/** De onde veio o avanço do item — a tela diz (RN-445). */
export type ItemProgressSource = 'COMODOS' | 'MEDICAO' | 'DIGITADO';

/**
 * O avanço que vale no item (RN-445, D9): os cômodos vencem a medição, e a medição vence o
 * digitado. Nada do que perde é apagado — a tela pode mostrar os três.
 */
export function itemProgress(item: {
  readonly progressFromRooms?: number;
  readonly progressFromExecution?: number;
  readonly progressPct: number;
}): { readonly value: number; readonly source: ItemProgressSource } {
  if (item.progressFromRooms !== undefined) {
    return { value: item.progressFromRooms, source: 'COMODOS' };
  }
  if (item.progressFromExecution !== undefined) {
    return { value: item.progressFromExecution, source: 'MEDICAO' };
  }
  return { value: item.progressPct, source: 'DIGITADO' };
}
